import React from "react";
import { HorizontalBar, Bar, Line } from "react-chartjs-2";
import createChartComponentsForDiagramConfigs from "./CaptionTableChartComponent.js";
import { shortenLabels, addCommasToLabels } from "Config/chartjs.global";
import Tables from "Components/Tables/SocioEconomicCharacteristics";
import Captions from "Captions";
import GenericTable from "Components/Tables/GenericTable";
import round from "Utils/round";
import config from "Config";
import { sortAgeGroups } from "Lookups";

const labels = (rows, attr) => [...new Set(rows.map((r) => r[attr]))];
const compareByLabels = (attr, labels) => (a, b) =>
  labels.indexOf(a[attr]) == labels.indexOf(b[attr])
    ? 0
    : labels.indexOf(a[attr]) < labels.indexOf(b[attr])
    ? -1
    : 1;

const fillMissing = (rows, labels, attr) => {
  let currRowsLabels = rows.map((r) => r[attr]);
  let missingLabels = labels.filter((l) => currRowsLabels.indexOf(l) == -1);
  missingLabels.map((l) => rows.push({ [attr]: l, number: 0 }));
  rows.sort(compareByLabels(attr, labels));
  return rows;
};

const DiagramData = {
  Education: {
    ChartComponent: HorizontalBar,
    TableComponent: GenericTable,
    queries: ["CATEGORY_EDUCATION"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] +
              _data.datasets[1].data[maxIndex] >
            b + _data.datasets[1].data[index]
              ? maxIndex
              : index,
          0
        );

        data.max_name =
          typeof _data.labels[maxIndex] === "object"
            ? _data.labels[maxIndex].join(" ")
            : _data.labels[maxIndex];
        data.max_count =
          _data.datasets[0].data[maxIndex] + _data.datasets[1].data[maxIndex];
        data.total_perc = round(1)(
          data.max_count /
            ((_data.total_population) / 100)
        );

        data.max_count_state = _data.rows_state.reduce(
          (sum, { tystap_main: name, number }) =>
            name == data.max_name ? sum + number : sum,
          0
        );

        data.total_perc_state = round(1)(
          data.max_count_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
    ]) => ({
      labels: labels(rows, "tystap_main"),
      total_population,
      total_population_state,
      rows_state,
      total_population_na,
      datasets: [
        {
          label: "Male",
          data: rows
            .filter(({ sexp_sex }) => sexp_sex == "Male")
            .chain((males) =>
              fillMissing(males, labels(rows, "tystap_main"), "tystap_main")
            )
            .map(({ number }) => number),
          backgroundColor: config.primary_chart_color,
        },
        {
          label: "Female",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter(({ sexp_sex }) => sexp_sex == "Female")
            .chain((females) =>
              fillMissing(females, labels(rows, "tystap_main"), "tystap_main")
            )
            .map(({ number }) => number),
        },
      ],
      table: { rows, fields, name: "Education" },
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
    footnote: [
      '"Other" is defined as "Other educational institution" under "Tertiary institution" see: http://tiny.cc/absedu',
    ],
  },
  Qualifications: {
    ChartComponent: HorizontalBar,
    TableComponent: GenericTable,
    queries: ["CATEGORY_QUALIFICATIONS"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] +
              _data.datasets[1].data[maxIndex] >
            b + _data.datasets[1].data[index]
              ? maxIndex
              : index,
          0
        );

        data.max_name =
          typeof _data.labels[maxIndex] === "object"
            ? _data.labels[maxIndex].join(" ")
            : _data.labels[maxIndex];
        data.max_count =
          _data.datasets[0].data[maxIndex] + _data.datasets[1].data[maxIndex];
        data.total_perc = round(1)(
          data.max_count /
            ((_data.total_population) / 100)
        );

        data.max_count_state = _data.rows_state.reduce(
          (sum, { qallp_main: name, number }) =>
            name == data.max_name ? sum + number : sum,
          0
        );

        data.total_perc_state = round(1)(
          data.max_count_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
    ]) => ({
      labels: [
        ...new Set(rows.map(({ qallp_main }) => qallp_main)),
      ].map((label) => shortenLabels(label)),
      total_population,
      total_population_state,
      rows_state,
      total_population_na,
      datasets: [
        {
          label: "Male",
          data: rows
            .filter(({ sexp_sex }) => sexp_sex == "Male")
            .map(({ number }) => number),
          backgroundColor: config.primary_chart_color,
        },
        {
          label: "Female",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter(({ sexp_sex }) => sexp_sex == "Female")
            .map(({ number }) => number),
        },
      ],
      table: { rows, fields, name: "Qualifications" },
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
  },
  "Labour Force Status": {
    ChartComponent: HorizontalBar,
    TableComponent: Tables["Labour Force Status"] || GenericTable,
    queries: ["CATEGORY_EMPLOYMENT_STATUS"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] +
              _data.datasets[1].data[maxIndex] >
            b + _data.datasets[1].data[index]
              ? maxIndex
              : index,
          0
        );

        data.max_name =
          typeof _data.labels[maxIndex] === "object"
            ? _data.labels[maxIndex].join(" ")
            : _data.labels[maxIndex];
        data.max_count =
          _data.datasets[0].data[maxIndex] + _data.datasets[1].data[maxIndex];
        data.total_perc = round(1)(
          data.max_count /
            ((_data.total_population) / 100)
        );

        data.max_count_state = _data.rows_state.reduce(
          (
            sum,
            { lfhrp_labour_force_status_and_hours_worked_not: name, number }
          ) => (name == data.max_name ? sum + number : sum),
          0
        );

        data.total_perc_state = round(1)(
          data.max_count_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
    ]) => ({
      labels: [
        ...new Set(
          rows.map(
            ({ lfhrp_labour_force_status_and_hours_worked_not }) =>
              lfhrp_labour_force_status_and_hours_worked_not
          )
        ),
      ].map((label) => shortenLabels(label)),
      total_population,
      total_population_state,
      rows_state,
      total_population_na,
      datasets: [
        {
          label: "Male",
          backgroundColor: config.primary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Male")
            .map((r) => r["number"]),
        },
        {
          label: "Female",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Female")
            .map((r) => r["number"]),
        },
      ],
      table: { rows, fields, name: "Labour Force Status" },
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
    height: 600,
  },
  Occupations: {
    ChartComponent: HorizontalBar,
    TableComponent: Tables["Occupations"] || GenericTable,
    queries: ["CATEGORY_OCCUPATION"],
    //footnote: ["Based on persons aged 15 years and over."],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] +
              _data.datasets[1].data[maxIndex] >
            b + _data.datasets[1].data[index]
              ? maxIndex
              : index,
          0
        );
        data.max_name =
          typeof _data.labels[maxIndex] === "object"
            ? _data.labels[maxIndex].join(" ")
            : _data.labels[maxIndex];
        data.max_count =
          _data.datasets[0].data[maxIndex] + _data.datasets[1].data[maxIndex];
        data.total_perc = round(1)(
          data.max_count /
            ((_data.total_population) / 100)
        );

        data.max_count_state = _data.rows_state.reduce(
          (sum, { occp_main: name, number }) =>
            name == data.max_name ? sum + number : sum,
          0
        );
        data.total_perc_state = round(1)(
          data.max_count_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
    ]) => ({
      labels: [
        ...new Set(rows.map(({ occp_main }) => occp_main)),
      ].map((label) => shortenLabels(label)),
      total_population,
      total_population_state,
      rows_state,
      total_population_na,
      datasets: [
        {
          label: "Male",
          backgroundColor: config.primary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Male")
            .map((r) => r["number"]),
        },
        {
          label: "Female",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Female")
            .map((r) => r["number"]),
        },
      ],
      table: { rows, fields, name: "Occupations" },
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
    height: 600,
  },
  "Voluntary Work": {
    ChartComponent: HorizontalBar,
    TableComponent: Tables["Voluntary Work"] || GenericTable,
    //footnote: ["Based on persons aged 15 years and over."],
    queries: ["CATEGORY_VOLUNTARY_WORK"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        data.number = _data.datasets[0].data
          .concat(_data.datasets[1].data)
          .reduce((sum, amount) => sum + amount, 0);
        data.total_perc = round(1)(
          data.number /
            ((_data.total_population) / 100)
        );

        data.number_state = _data.rows_state.reduce(
          (sum, { number }) => sum + number,
          0
        );

        data.total_perc_state = round(1)(
          data.number_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
    ]) => ({
      labels: [...new Set(rows.map(({ workage10 }) => workage10))],
      total_population,
      total_population_state,
      rows_state,
      total_population_na,
      datasets: [
        {
          label: "Male",
          backgroundColor: config.primary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Male")
            .map((r) => r["number"]),
        },
        {
          label: "Female",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Female")
            .map((r) => r["number"]),
        },
      ],
      table: {
        rows,
        fields,
        name: "Voluntary Work",
        sortFunc: {
          workage10: sortAgeGroups("workage10"),
        },
      },
    }),
    chartOptions: {
      scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
  },
  "Personal Income": {
    ChartComponent: HorizontalBar,
    TableComponent: Tables["Personal Income"] || GenericTable,
    //footnote: ["Based on persons aged 15 years and over."],
    queries: ["CATEGORY_INCOME"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] +
              _data.datasets[1].data[maxIndex] >
            b + _data.datasets[1].data[index]
              ? maxIndex
              : index,
          0
        );

        data.max_name =
          typeof _data.labels[maxIndex] === "object"
            ? _data.labels[maxIndex].join(" ")
            : _data.labels[maxIndex];
        data.max_count =
          _data.datasets[0].data[maxIndex] + _data.datasets[1].data[maxIndex];
        data.total_perc = round(1)(
          data.max_count /
            ((_data.total_population) / 100)
        );
        data.max_count_state = _data.rows_state.reduce(
          (sum, { incp_total_personal_income_weekly: name, number }) =>
            name == data.max_name ? sum + number : sum,
          0
        );
        data.total_perc_state = round(1)(
          data.max_count_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
    ]) =>
      ({
        labels: [
          ...new Set(rows.map((r) => r["incp_total_personal_income_weekly"])),
        ], // create labels
        sortFunc: (labels, a, b, label_column) =>
          labels.indexOf(a[label_column]) > labels.indexOf(b[label_column])
            ? 1
            : labels.indexOf(b[label_column]) > labels.indexOf(a[label_column])
            ? -1
            : 0,
      }.chain(({ labels, sortFunc }) => ({
        labels: labels,
        total_population,
        total_population_state,
        rows_state,
        datasets: [
          {
            label: "Male",
            backgroundColor: config.primary_chart_color,
            data: rows
              .filter((r) => r["sexp_sex"] == "Male")
              .sort((a, b) =>
                sortFunc(labels, a, b, "incp_total_personal_income_weekly")
              )
              .map((r) => r["number"]),
          },
          {
            label: "Female",
            backgroundColor: config.secondary_chart_color,
            data: rows
              .filter((r) => r["sexp_sex"] == "Female")
              .sort((a, b) =>
                sortFunc(labels, a, b, "incp_total_personal_income_weekly")
              )
              .map((r) => r["number"]),
          },
        ],
        table: {
          rows,
          fields,
          name: "Personal Income",
        },
      }))),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
  },
}.tap((diagrams) =>
  Object.keys(Captions["Socio–economic Characteristics"]).forEach(
    (key) =>
      (diagrams[key].captions = Captions["Socio–economic Characteristics"][key])
  )
);

export default createChartComponentsForDiagramConfigs(DiagramData);



// WEBPACK FOOTER //
// ./components/Charts/SocioEconomicCharacteristics.js